// frontend/src/pages/dashboards/DashboardTile.jsx
// Phase 4 step 14a-3: what sits inside one cell of the dashboard grid. A
// chart tile has a title row (the tile's title or the chart's, a link to the
// chart) and the chart drawn by ChartView from the chart's saved type and
// presentation, exactly as its own page draws it. A text tile is plain text.
// In edit mode a tile gets its controls (title, text, remove); the grid itself
// handles moving and resizing. Controls keep clear of the bottom-right corner,
// where the grid's resize handle sits.
//
// Step 14b-2: a chart tile is drawn through the filter bar. `request` is
// this tile's part of it (filterState.requestFilters: the conditions, which
// filter each came from, the filters this tile was set to ignore); the tile
// says "Not filtered by …" for active filters that do not reach its chart.
// In edit mode a "Filters" control opens the tile's filter settings.
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FilterX, Pencil, SlidersHorizontal, X } from 'lucide-react';
import ChartView from '../../charts/ChartView';
import { ROW_HEIGHT, spanPx } from './gridLayout';
import { TILE_HEADER_PX, tileTitle, tileViewProps } from './dashboardState';
import { notFilteredBy, notFilteredText } from './filterState';

const PANEL = 'flex h-full min-w-0 flex-col overflow-hidden rounded-[22px] bg-[var(--ui-panel)] text-[var(--ui-text)] shadow-[inset_0_1px_0_var(--ui-panel-highlight)]';
const NOTE_MIN_WIDTH = 5;     // grid columns a tile needs before "Not filtered by …" is written out
const ICON_BUTTON = 'rounded-md p-1 text-[var(--ui-text-muted)] hover:bg-[var(--ui-inset-hover)] hover:text-[var(--ui-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';

function EditControls({ onEdit, editLabel, onRemove, onFilters }) {
  return (
    <span className="flex shrink-0 items-center gap-0.5" data-no-drag="">
      {onFilters && (
        <button type="button" onClick={onFilters} aria-label="Tile filters" title="Which filters narrow this tile" className={ICON_BUTTON}>
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
      {onEdit && (
        <button type="button" onClick={onEdit} aria-label={editLabel} title={editLabel} className={ICON_BUTTON}>
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
      <button type="button" onClick={onRemove} aria-label="Remove tile" title="Remove from dashboard" className={ICON_BUTTON}>
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

/**
 * tile      the grid's tile ({ key, kind, x, y, w, h, chartId, chart, text, settings })
 * editing   edit mode: show the tile's controls
 * dragging  this tile is being moved: the chart dims (it stays mounted, so nothing is fetched again)
 * onEditText(tile), onEditTitle(tile), onRemove(tile)
 * defs      the dashboard's filter definitions (14b-2)
 * request   { filters, owners, optedOut } for this tile, or null (no filters: fetched as before)
 * onEditFilters(tile)  edit mode, when the dashboard has filters
 */
export default function DashboardTile({
  tile, editing = false, dragging = false, onEditText, onEditTitle, onRemove, defs = [], request = null, onEditFilters = null,
}) {
  const height = spanPx(tile.h, ROW_HEIGHT);
  const [answer, setAnswer] = useState({ report: null, key: null });
  const onNarrowing = useCallback((report, key) => setAnswer({ report, key }), []);

  if (tile.kind === 'text') {
    const heading = tile.h <= 1;
    return (
      <div className={`${PANEL} justify-center px-5 ${heading ? 'py-0' : 'py-4'}`} role="note">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <p className={`min-w-0 whitespace-pre-wrap break-words ${heading ? 'truncate text-[15px] font-semibold tracking-[-0.01em]' : 'text-sm leading-relaxed text-[var(--ui-text)]'} ${editing ? 'pr-6' : ''}`}>
            {tile.text}
          </p>
          {editing && <EditControls onEdit={() => onEditText(tile)} editLabel="Edit text" onRemove={() => onRemove(tile)} />}
        </div>
      </div>
    );
  }

  const title = tileTitle(tile);
  const view = tileViewProps(tile);
  const bodyHeight = Math.max(60, height - TILE_HEADER_PX - 16);
  // The server's report counts only when it answers the filters shown now; the opt-outs are known here.
  const requestKey = request ? JSON.stringify(request.filters) : null;
  const report = answer.key === requestKey ? answer.report : null;
  const ignored = request && tile.chart ? notFilteredText(notFilteredBy(defs, request.owners, report, request.optedOut)) : '';

  return (
    <div className={PANEL}>
      <div className="flex h-10 shrink-0 items-center justify-between gap-2 px-4 pt-1" style={{ height: TILE_HEADER_PX }}>
        <h3 className="min-w-0 truncate text-[13px] font-semibold tracking-[-0.01em]" title={title}>{title}</h3>
        <span className="flex min-w-0 shrink items-center gap-0.5">
          {ignored && (
            // On a narrow tile only the icon shows (the words are its tooltip and are read out), so the title keeps its room.
            <span className="mr-1 flex min-w-0 items-center gap-1 text-[11px] text-[var(--ui-text-muted)]" title={ignored} data-tile-ignored="">
              <FilterX className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className={tile.w >= NOTE_MIN_WIDTH ? 'truncate' : 'sr-only'}>{ignored}</span>
            </span>
          )}
          {tile.chart && !editing && (
            <Link to={`/charts/${tile.chart.id}`} aria-label={`Open the chart ${tile.chart.title}`} title="Open the chart" className={ICON_BUTTON}>
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
          {editing && (
            <EditControls onEdit={tile.chart ? () => onEditTitle(tile) : null} editLabel="Tile title" onRemove={() => onRemove(tile)}
              onFilters={tile.chart && onEditFilters && defs.length ? () => onEditFilters(tile) : null} />
          )}
        </span>
      </div>
      <div className={`min-h-0 flex-1 px-3 pb-2 ${editing ? 'pointer-events-none select-none' : ''}`}>
        {!view && (
          <div style={{ height: bodyHeight }} className="flex flex-col items-center justify-center gap-2 px-6 text-center">
            <p className="text-sm text-[var(--ui-text-muted)]">This chart is no longer available.</p>
            {editing && <p className="text-xs text-[var(--ui-text-muted)]">Remove the tile, or leave it as a placeholder.</p>}
          </div>
        )}
        {view && (
          <div className={dragging ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
            <ChartView chartId={tile.chart.id} type={view.type} presentation={view.presentation} matrix={view.matrix}
              height={bodyHeight} showTableToggle={false} filters={request ? request.filters : null} onNarrowing={onNarrowing} />
          </div>
        )}
      </div>
    </div>
  );
}
